import {
  ArrowRightIcon,
  CalculatorIcon,
  CameraIcon,
  Globe,
  HeartIcon,
  MinimizeIcon,
  QrCode,
  SplitIcon,
  UsersIcon,
  WifiOffIcon,
} from 'lucide-react';

import { useEffect, useState } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import {
  GROUP_CATEGORIES,
  getGroupCategoryGradient,
} from '@splitfiesta/shared';

import { LanguageSwitch } from '@/components/common/LanguageSwitch';
import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { SplitFiestaLoading } from '@/components/common/SplitFiestaLoading';
import { StoreButtons } from '@/components/common/StoreButtons';
import { StoreQrCodes } from '@/components/common/StoreQrCodes';
import { Wordmark } from '@/components/common/Wordmark';
import { Footer } from '@/components/layout/Footer';
import { CheckmarkIcon } from '@/components/ui/CheckmarkIcon';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

import { useRouteChange } from '@/hooks/useRouteChangeHandler';
import { useRouter } from '@/hooks/useRouteChangeRouter';

import { isEmbeddedInMobileApp } from '@/lib/embeddedMode';

import { useAuthStore } from '@/stores/authStore';

import { getGroupCategoryIcon } from '@/utils/groupCategoryIcons';

interface WebVersionLinkProps {
  className?: string;
  /** Light text for the orange CTA band, default grey for the hero. */
  tone?: 'default' | 'onColor';
}

/**
 * The browser path, kept available but visibly secondary to the badges. One
 * link to sign-in only: no register link, so new people start in the app.
 */
function WebVersionLink({
  className = '',
  tone = 'default',
}: WebVersionLinkProps) {
  const { t } = useTranslation('landing');
  const text =
    tone === 'onColor' ? 'text-white' : 'text-gray-500 dark:text-gray-400';
  const link =
    tone === 'onColor'
      ? 'text-white font-semibold underline hover:no-underline'
      : 'text-orange-600 dark:text-orange-400 font-medium underline-offset-2 hover:underline';
  return (
    <p
      className={`text-sm ${text} ${className}`}
      data-testid="landing-web-version"
    >
      {t('webVersion.promptBefore')}{' '}
      <RouteChangeLink
        href="/login"
        className={link}
        data-testid="landing-login-link"
      >
        {t('webVersion.link')}
      </RouteChangeLink>
      {t('webVersion.promptAfter')}
    </p>
  );
}

export function LandingPage() {
  const user = useAuthStore(state => state.user);
  const loading = useAuthStore(state => state.isLoading);
  const router = useRouter();
  const { t, ready } = useTranslation();
  const { isChangingRoute } = useRouteChange();
  const [mounted, setMounted] = useState(false);
  const isEmbedded = isEmbeddedInMobileApp();

  // Load landing namespace
  const landingLoaded = useNamespace('landing');
  const groupsLoaded = useNamespace('groups');

  useEffect(() => {
    // Use setTimeout to avoid setting state during render
    const timer = setTimeout(() => {
      setMounted(true);
    }, 0);

    // Remove loading class when component is mounted
    if (document.documentElement.classList.contains('loading')) {
      document.documentElement.classList.remove('loading');
    }

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!loading && user) {
      router.push('/groups');
    }
  }, [user, loading, router]);

  if (loading || !ready || !landingLoaded || !groupsLoaded || !mounted) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          backgroundColor:
            typeof window !== 'undefined' &&
            window.matchMedia('(prefers-color-scheme: dark)').matches
              ? 'rgb(17, 24, 39)' // gray-900
              : '#fcfcfc',
        }}
      >
        <div className="text-center">
          <div className="loading-spinner-dark w-10 h-10 mx-auto mb-3"></div>
          <h1 className="flex justify-center">
            <Wordmark height={28} pulse />
          </h1>
        </div>
      </div>
    );
  }

  // Show loading when navigating to auth pages
  if (isChangingRoute) {
    // Add loading class to show scrollbars
    if (
      typeof window !== 'undefined' &&
      !document.documentElement.classList.contains('loading')
    ) {
      document.documentElement.classList.add('loading');
    }
    return <SplitFiestaLoading />;
  }

  const features = [
    {
      icon: SplitIcon,
      title: t('landing:features.items.smartSplitting.title'),
      description: t('landing:features.items.smartSplitting.description'),
    },
    {
      icon: HeartIcon,
      title: t('landing:features.items.socialFeatures.title'),
      description: t('landing:features.items.socialFeatures.description'),
    },
    {
      icon: WifiOffIcon,
      title: t('landing:features.items.offlineMode.title'),
      description: t('landing:features.items.offlineMode.description'),
    },
    {
      icon: QrCode,
      title: t('landing:features.items.inviteQr.title'),
      description: t('landing:features.items.inviteQr.description'),
    },
    {
      icon: MinimizeIcon,
      title: t('landing:features.items.debtSimplification.title'),
      description: t('landing:features.items.debtSimplification.description'),
    },
    {
      icon: CameraIcon,
      title: t('landing:features.items.receiptScanning.title'),
      description: t('landing:features.items.receiptScanning.description'),
    },
    {
      icon: Globe,
      title: t('landing:features.items.multiCurrency.title'),
      description: t('landing:features.items.multiCurrency.description'),
    },
  ];

  return (
    <div
      className="min-h-screen bg-canvas dark:bg-canvas-dark"
      data-testid="landing-page"
    >
      {/* Navigation */}
      <nav
        className="fixed top-0 left-0 right-0 bg-white/98 dark:bg-gray-900/98 backdrop-blur-md z-50 border-b border-orange-100/30 dark:border-gray-800 shadow-xs dark:shadow-gray-900/20"
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="relative">
              <h1 className="flex items-center">
                <Wordmark height={22} />
              </h1>
            </div>
            <div className="flex items-center justify-end space-x-1 sm:space-x-3 -mr-2 sm:-mr-3">
              <LanguageSwitch variant="clean" />
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-linear-to-br from-orange-50/30 via-yellow-50/20 to-red-50/20 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 relative overflow-hidden"
        data-testid="landing-hero"
      >
        {/* Decorative elements */}
        <div className="absolute inset-0 bg-linear-to-br from-yellow-100/10 via-transparent to-orange-100/10 dark:from-orange-900/5 dark:via-transparent dark:to-red-900/5"></div>
        <div className="absolute top-10 left-10 w-20 h-20 bg-orange-200/15 dark:bg-orange-600/10 rounded-full blur-xl"></div>
        <div className="absolute top-32 right-20 w-32 h-32 bg-yellow-200/15 dark:bg-yellow-600/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-red-200/15 dark:bg-red-600/10 rounded-full blur-xl"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              {t('landing:hero.title')}{' '}
              <span className="bg-linear-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                {t('landing:hero.titleHighlight')}
              </span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
              {t('landing:hero.subtitle')
                .split('**')
                .map((part, index) =>
                  index % 2 === 1 ? (
                    <span
                      key={index}
                      className="font-bold text-gray-900 dark:text-gray-100"
                    >
                      {part}
                    </span>
                  ) : (
                    part
                  )
                )}
            </p>
            <StoreButtons className="mt-2 justify-center" />
            <StoreQrCodes className="mt-6" />
            {!isEmbedded && <WebVersionLink className="mt-4" />}
          </div>
        </div>
      </section>

      {/* Use Cases Section - Who is this for? */}
      <section
        id="use-cases"
        className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900"
        data-testid="landing-use-cases"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {t('landing:useCases.title')}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('landing:useCases.subtitle')}
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-8">
            {GROUP_CATEGORIES.filter(category => category !== 'other').map(
              category => {
                const Icon = getGroupCategoryIcon(category);
                const [from, to] = getGroupCategoryGradient(category);
                return (
                  <div
                    key={category}
                    data-testid={`use-case-${category}`}
                    className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.334rem)] flex items-start gap-4 p-2"
                  >
                    <div
                      data-testid={`use-case-badge-${category}`}
                      className="w-14 h-14 shrink-0 rounded-full flex items-center justify-center shadow-md"
                      style={{
                        background: `linear-gradient(135deg, ${from}, ${to})`,
                      }}
                    >
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        {t(`groups:categories.${category}`)}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {t(`groups:categoryExamples.${category}`)}
                      </p>
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>
      </section>

      {/* How It Works Section - How does it work? */}
      <section
        id="how-it-works"
        className="py-20 px-4 sm:px-6 lg:px-8 bg-linear-to-br from-yellow-50/20 via-orange-50/15 to-red-50/20 dark:from-gray-800/50 dark:via-gray-800/30 dark:to-gray-800/50"
        data-testid="landing-how-it-works"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {t('landing:howItWorks.title')}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              {t('landing:howItWorks.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                number: t('landing:howItWorks.steps.createGroup.number'),
                title: t('landing:howItWorks.steps.createGroup.title'),
                description: t(
                  'landing:howItWorks.steps.createGroup.description'
                ),
                icon: UsersIcon,
              },
              {
                number: t('landing:howItWorks.steps.addExpenses.number'),
                title: t('landing:howItWorks.steps.addExpenses.title'),
                description: t(
                  'landing:howItWorks.steps.addExpenses.description'
                ),
                icon: CalculatorIcon,
              },
              {
                number: t('landing:howItWorks.steps.settleUp.number'),
                title: t('landing:howItWorks.steps.settleUp.title'),
                description: t('landing:howItWorks.steps.settleUp.description'),
                icon: CheckmarkIcon,
              },
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative">
                  <div className="w-16 h-16 bg-linear-to-br from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold shadow-lg">
                    {step.number}
                  </div>
                  {index < 2 && (
                    <ArrowRightIcon className="hidden md:block absolute top-8 -right-4 w-8 h-8 text-orange-300 dark:text-orange-600" />
                  )}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - What can it do? */}
      <section
        id="features"
        className="py-20 px-4 sm:px-6 lg:px-8 bg-linear-to-br from-red-50/20 to-orange-50/20 dark:from-gray-900 dark:to-gray-900"
        data-testid="landing-features"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {t('landing:features.title')}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('landing:features.subtitle')}
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                data-testid={`feature-${index}`}
                className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.334rem)] flex items-start gap-4 p-2"
              >
                <feature.icon className="w-8 h-8 shrink-0 mt-0.5 text-orange-500 dark:text-orange-400" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section — hidden in embedded mode (user is already authenticated in the native app) */}
      {!isEmbedded && (
        <section
          className="py-20 px-4 sm:px-6 lg:px-8 bg-linear-to-r from-orange-500 via-red-500 to-orange-600 relative overflow-hidden"
          data-testid="landing-cta"
        >
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full bg-linear-to-br from-black/10 to-black/20"></div>
          <div className="absolute top-10 right-10 w-32 h-32 bg-orange-400/20 rounded-full blur-2xl"></div>
          <div className="absolute bottom-10 left-10 w-24 h-24 bg-red-400/20 rounded-full blur-xl"></div>

          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h2 className="text-4xl font-bold text-white mb-4 drop-shadow-lg">
              {t('landing:cta.title')}
            </h2>
            <p className="text-xl text-white mb-8">
              {t('landing:cta.subtitle')}
            </p>
            <StoreButtons className="justify-center" />
            <WebVersionLink className="mt-6" tone="onColor" />
          </div>
        </section>
      )}

      {/* Footer */}
      {!isEmbedded && <Footer />}
    </div>
  );
}
